import _ from "lodash";
import React from "react";
import styled from "styled-components";
import useOpenDocument from "../../../hooks/useOpenDocument";
import { useAuth } from "react-oidc-context";
import { openPdfViewerModal } from "../../../components/modals/ViewPdfModal";
import { openEditClientReportModal } from "../../../components/modals/client-report-edit";
import { useClientReportsList } from "../../../hooks/ClientReportContext";
import { useBusinessSheetData } from "../../../hooks/useBusinessSheetData";
import { useReportPdfData } from "../../../hooks/useReportPdfData";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const ActionColumnParent = styled.td`
    width: ${({ width }) => width}%;
    cursor: pointer;
    vertical-align: middle;
    text-align: ${props => props.textAlign ?? "left"};
`;

const ActionColumnParentToggle = React.forwardRef(({ ...rest }, ref) => (
    <div ref={ref} {...rest}>
        <FontAwesomeIcon icon="fa-ellipsis" />
    </div>
));

const ReportsTableActionColumn = ({ width = 2.5, data = null }) => {
    const menuId = React.useId();
    const { user } = useAuth();
    const { openDocument, isFetching: isOpening } = useOpenDocument();
    const { getPdfBytes } = useBusinessSheetData(data?.businessSheet?.businessSheetId);
    const { generatePdf: generateReportPdf, getPdfBytes: getReportPdfBytes } = useReportPdfData(data?.masterAccountId, data?.id);
    const { showHistoryModal } = useClientReportsList();
    const roles = user?.profile?.roles ?? [];
    const canSeeAdminControls = _.some(roles, (item) => ['sys_admin_access'].includes(item));

    const onGenerateReportPdf = () => {
        if (!canSeeAdminControls) {
            return;
        }
        generateReportPdf();
    }

    const handleOpenBusinessSheet = () => {
        getPdfBytes().then(
            r => openPdfViewerModal({ data: r, title: data ? `View: ${data.reportName}` : 'View Report' })
        );
    };

    const handleOpenDocument = () => openDocument(data['documentId']);

    const handleOpenEditModal = () => {
        openEditClientReportModal({ defaultValues: data });
    };

    const handleOpenHistoryModal = () => showHistoryModal(data);

    const handleOpenReportPdfModal = () => {
        getReportPdfBytes().then(
            r => openPdfViewerModal({ data: r, title: data ? `View: ${data.reportName}` : 'View Report' })
        );
    };

    if (!data) {
        return (
            <ActionColumnParent width={width}>
                <span>-</span>
            </ActionColumnParent>
        );
    }

    const { businessSheet, documentPdfId, isPdfGenerated, isStatusTerminated } = data;
    return (
        <ActionColumnParent width={width}>
            <Dropdown className="caret-off">
                <Dropdown.Toggle id={menuId} as={ActionColumnParentToggle} />
                <Dropdown.Menu style={{ maxWidth: '14rem' }}>
                    <Dropdown.Header className="text-ellipsis">{data['reportName']}</Dropdown.Header>
                    {isStatusTerminated === false && (
                        <Dropdown.Item disabled={isOpening} onClick={handleOpenDocument}>
                            <FontAwesomeIcon icon="fas fa-file-signature" size="sm" /> <span className="ms-1">Edit Report</span>
                        </Dropdown.Item>
                    )}
                    {isStatusTerminated === true && (
                        <Dropdown.Item disabled={!isPdfGenerated || !documentPdfId} onClick={handleOpenReportPdfModal}>
                            <FontAwesomeIcon icon="fas fa-file-pdf" size="sm" /> <span className="ms-1">View Report</span>
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={handleOpenHistoryModal}>
                        <FontAwesomeIcon icon="fas fa-history" size="sm" /> <span className="ms-1">View History</span>
                    </Dropdown.Item>
                    {businessSheet && (
                        <Dropdown.Item onClick={handleOpenBusinessSheet}>
                            <FontAwesomeIcon icon="fa-file-circle-check" size="sm" /> <span className="ms-1">View Business Sheet</span>
                        </Dropdown.Item>
                    )}
                    <Dropdown.Header>
                        Tools
                    </Dropdown.Header>
                    <Dropdown.Item onClick={handleOpenEditModal}>
                        <FontAwesomeIcon icon="fa fa-pencil" size="sm" /> <span className="ms-1">Edit Details</span>
                    </Dropdown.Item>
                    {canSeeAdminControls && (
                        <>
                            {isStatusTerminated === true && (!isPdfGenerated || !documentPdfId) && (
                                <Dropdown.Item onClick={onGenerateReportPdf}>
                                    <FontAwesomeIcon icon="fas fa-file-pdf" size="sm" /> <span className="ms-1">Generate PDF</span>
                                </Dropdown.Item>
                            )}
                        </>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </ActionColumnParent>
    );
};

export default ReportsTableActionColumn;